import { SYSTEM_PROMPT } from '../constants/prompts.js';
import { GEMINI_API_CONFIG, API_ERROR_MESSAGES } from './constants.js';

/**
 * Gemini APIを使用してAIの応答を生成
 * @param {string} userMessage - ユーザーのメッセージ
 * @param {Array} conversationHistory - 会話履歴
 * @param {string} apiKey - APIキー
 * @param {AbortController} controller - 中断コントローラー
 * @returns {Promise<string>} AIの応答
 */
export const generateAIResponse = async (userMessage, conversationHistory, apiKey, controller) => {
  if (!apiKey) {
    throw new Error(API_ERROR_MESSAGES.noApiKey);
  }

  // 会話履歴を構築
  const messages = conversationHistory.map(msg => ({
    role: msg.role === 'user' ? 'user' : 'model',
    parts: [{ text: msg.content }]
  }));

  // 現在のユーザーメッセージを追加
  messages.push({
    role: 'user',
    parts: [{ text: userMessage }]
  });

  // システムプロンプトを最初に追加
  const contents = [
    {
      role: 'user',
      parts: [{ text: SYSTEM_PROMPT }]
    },
    {
      role: 'model',
      parts: [{ text: '了解しました。戦略的キャリアアドバイザーとして、客観的な事実に基づいてアドバイスさせていただきます。' }]
    },
    ...messages
  ];

  try {
    const response = await fetch(
      `${GEMINI_API_CONFIG.baseURL}/models/${GEMINI_API_CONFIG.model}:generateContent?key=${apiKey}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          contents: contents,
          generationConfig: GEMINI_API_CONFIG.generationConfig,
          safetySettings: GEMINI_API_CONFIG.safetySettings
        }),
        signal: controller?.signal
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      if (response.status === 400 && errorData.error?.message?.includes('API_KEY_INVALID')) {
        throw new Error(API_ERROR_MESSAGES.invalidApiKey);
      }
      throw new Error(errorData.error?.message || API_ERROR_MESSAGES.generic);
    }

    const data = await response.json();
    
    if (!data.candidates || data.candidates.length === 0) {
      throw new Error(API_ERROR_MESSAGES.noResponse);
    }

    return data.candidates[0].content.parts[0].text;
  } catch (error) {
    if (error.name === 'AbortError') {
      throw new Error(API_ERROR_MESSAGES.aborted);
    }
    console.error('Gemini API Error:', error);
    if (error instanceof Error) {
      throw error;
    }
    throw new Error(API_ERROR_MESSAGES.unexpected);
  }
};

/**
 * 会話からサマリーを生成
 * @param {Array} messages - メッセージ配列
 * @param {string} apiKey - APIキー
 * @param {AbortController} controller - 中断コントローラー
 * @returns {Promise<string|null>} 生成されたサマリー
 */
export const generateConversationSummary = async (messages, apiKey, controller) => {
  if (!apiKey || messages.length < 2) {
    return null;
  }

  // 最初の数回のやり取りを抽出（最大4メッセージ）
  const relevantMessages = messages.slice(0, 4).map(msg => ({
    role: msg.role,
    content: msg.content
  }));

  const summaryPrompt = `以下の会話の内容から、簡潔で分かりやすい要約を20文字以内で作成してください。キャリア相談の内容を具体的に示すタイトルのような形式で答えてください。

会話内容:
${relevantMessages.map(msg => `${msg.role === 'user' ? 'ユーザー' : 'アドバイザー'}: ${msg.content}`).join('\n\n')}

要約（20文字以内）:`;

  try {
    const response = await fetch(
      `${GEMINI_API_CONFIG.baseURL}/models/${GEMINI_API_CONFIG.model}:generateContent?key=${apiKey}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          contents: [{
            role: 'user',
            parts: [{ text: summaryPrompt }]
          }],
          generationConfig: GEMINI_API_CONFIG.summaryConfig,
          safetySettings: GEMINI_API_CONFIG.safetySettings
        }),
        signal: controller?.signal
      }
    );

    if (!response.ok) {
      throw new Error(API_ERROR_MESSAGES.summaryFailed);
    }

    const data = await response.json();
    
    if (!data.candidates || data.candidates.length === 0) {
      return null;
    }

    return data.candidates[0].content.parts[0].text.trim();
  } catch (error) {
    if (error.name === 'AbortError') {
      return null;
    }
    console.error('Summary generation error:', error);
    return null;
  }
};